import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, User, ArrowRight } from '@phosphor-icons/react';
import { NewsListing } from '../components/NewsListing';
import axios from 'axios';
import { Seo } from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function NewsArticleView({ slug }) {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const r = await axios.get(`${API}/news/${slug}`);
        setArticle(r.data);
      } catch {
        setArticle(null);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  if (loading) return (
    <main className="pt-24 bg-white min-h-screen">
      <div className="max-w-4xl mx-auto px-6 py-16 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-lime-600 border-t-transparent mx-auto"></div>
      </div>
    </main>
  );

  if (!article) return (
    <main className="pt-24 bg-white min-h-screen">
      <div className="max-w-4xl mx-auto px-6 py-16 text-center">
        <h1 className="font-outfit font-bold text-2xl text-neutral-900 mb-4">Article Not Found</h1>
        <p className="text-neutral-600 mb-6">The article you're looking for doesn't exist.</p>
        <Link to="/news" className="text-lime-600 font-medium hover:underline inline-flex items-center gap-2">
          <ArrowLeft size={18} /> Back to News
        </Link>
      </div>
    </main>
  );

  return (
    <main className="pt-24 bg-white" data-testid="news-article-view">
      {/* Hero Image */}
      {article.image_url && (
        <section className="relative h-64 sm:h-80 lg:h-[28rem] overflow-hidden bg-neutral-100">
          <img
            src={article.image_url}
            alt={article.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/70 via-transparent to-transparent" />
        </section>
      )}

      {/* Article Content */}
      <section className="py-12 lg:py-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          {/* Back Link */}
          <Link
            to="/news"
            className="inline-flex items-center gap-2 text-lime-600 font-medium hover:text-lime-700 transition-colors mb-8"
            data-testid="news-back-link"
          >
            <ArrowLeft size={18} weight="bold" />
            Back to News
          </Link>

          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Meta */}
            <div className="flex items-center gap-4 text-neutral-500 text-sm mb-4">
              {article.date && (
                <div className="flex items-center gap-1.5">
                  <Calendar size={16} weight="duotone" className="text-lime-600" />
                  <span>{article.date}</span>
                </div>
              )}
              {article.author && (
                <>
                  <span className="text-neutral-300">|</span>
                  <div className="flex items-center gap-1.5">
                    <User size={16} weight="duotone" className="text-lime-600" />
                    <span>{article.author}</span>
                  </div>
                </>
              )}
            </div>

            {/* Title */}
            <h1
              className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl text-neutral-900 tracking-tight mb-8"
              data-testid="news-article-title"
            >
              {article.title}
            </h1>

            {/* Excerpt as lead */}
            {article.excerpt && (
              <p className="text-lg text-neutral-600 leading-relaxed mb-8 border-l-4 border-lime-500 pl-5 italic">
                {article.excerpt}
              </p>
            )}

            {/* Full Content */}
            {article.content ? (
              <div className="prose prose-neutral prose-lg max-w-none" data-testid="news-article-content">
                {article.content.split('\n\n').map((paragraph, i) => (
                  <p key={i} className="text-neutral-700 leading-relaxed mb-5">
                    {paragraph}
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-neutral-600 leading-relaxed">{article.excerpt}</p>
            )}
          </motion.article>

          {/* CTA */}
          <div className="mt-12 pt-8 border-t border-neutral-200">
            <div className="flex items-center justify-between">
              <Link
                to="/news"
                className="inline-flex items-center gap-2 text-lime-600 font-medium hover:text-lime-700 transition-colors"
              >
                <ArrowLeft size={18} weight="bold" />
                All News
              </Link>
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-2"
              >
                Get in Touch
                <ArrowRight size={18} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default function NewsPage() {
  const { slug } = useParams();

  if (slug) return <NewsArticleView slug={slug} />;

  return (
    <main className="pt-24 bg-white" data-testid="news-page">
      <Seo title="News" description="Latest news, awards and updates from North Somerset Environment Company." path="/news" />
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="accent-font text-lime-600 mb-4">News & Updates</p>
            <h1 className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl text-neutral-900 tracking-tight mb-6">
              Latest <span className="text-lime-600">News</span>
            </h1>
            <p className="text-neutral-600 text-lg max-w-3xl">
              Stay up to date with the latest news, announcements, and updates from
              North Somerset Environment Company.
            </p>
          </motion.div>
        </div>
      </section>

      {/* News Grid */}
      <NewsListing />
    </main>
  );
}
